import { Dispatch } from 'redux';
import { useDispatch } from 'react-redux';
import { useMemo } from 'react';
import CrudActionContainer from './CrudActionContainer';
import commonHandler, {
  permissionsHandler,
} from '../errorHandlers/common-handler';
import { CtmAdController } from '../controllers/CtmAdController';
import { StoreState } from '../store-interfaces';
import { CtmAdEntity, CtmAdIndexModel, CtmAdModel } from '../model/CtmAdModel';
import { selectCtmAd, unselectCtmAd } from './viewActions';
import AdConfigModel from '../model/AdConfigModel';
import { MessengerAdModel } from '../model/MessengerAdModel';

class CtmAdCRUDActions extends CrudActionContainer {
  constructor() {
    super('CTM_ADS');
  }

  create(botId: number, entity: CtmAdEntity) {
    return async (dispatch: Dispatch<StoreState>) => {
      try {
        dispatch(this.busy(this.createAction));
        const response = await CtmAdController.postCtmAd(botId, entity);
        const { ctmAd } = response;
        dispatch(this.retrieved(ctmAd));
        dispatch(selectCtmAd(ctmAd));
        return ctmAd;
      } catch (e) {
        commonHandler(dispatch)(e);
      }
    };
  }

  retrieveAll(botId: number) {
    return async (dispatch: Dispatch<StoreState>) => {
      dispatch(this.busy(this.retrieveAllAction));
      try {
        const response: {
          ctmAds: CtmAdIndexModel[];
        } = await CtmAdController.getAll(botId);
        const { ctmAds } = response;
        dispatch(this.retrievedAll(ctmAds));
        return ctmAds;
      } catch (e) {
        commonHandler(dispatch)(e);
      }
    };
  }

  retrieveById(botId: number, ctmAdId: number) {
    return async (dispatch: Dispatch<StoreState>) => {
      dispatch(this.busy(this.retrieveAction));
      try {
        const response = await CtmAdController.getById(botId, ctmAdId);
        const { ctmAd } = response;
        dispatch(this.updateLocal(ctmAd));
        dispatch(selectCtmAd(ctmAd));
        return ctmAd;
      } catch (e) {
        if (e instanceof Response) {
          if (e.status === 404) {
            return;
          }
        }
        return permissionsHandler(dispatch, this)(e);
      }
    };
  }

  update(
    botId: number,
    entity: (Partial<AdConfigModel> | Partial<MessengerAdModel>) & {
      id: number;
    },
    draft: boolean = true,
  ) {
    return async (dispatch: Dispatch<StoreState>) => {
      dispatch(this.busy(this.updateAction));
      try {
        const response = await CtmAdController.putCtmAd(botId, entity);
        const { ctmAd } = response;
        if (draft) {
          dispatch(this.updateLocal(ctmAd));
          dispatch(selectCtmAd(ctmAd));
        }
        return ctmAd;
      } catch (e) {
        return commonHandler(dispatch)(e);
      }
    };
  }

  delete(botId: number, ctmAd: CtmAdModel) {
    return async (dispatch: Dispatch<StoreState>) => {
      dispatch(this.busy(this.deleteLocalAction));
      try {
        const response = await CtmAdController.deleteCtmAd(botId, ctmAd.id);
        dispatch(this.deleteLocal(ctmAd));
        dispatch(unselectCtmAd());
        return response;
      } catch (e) {
        commonHandler(dispatch)(e);
      }
    };
  }
}

const ctmAdCRUDActions = new CtmAdCRUDActions();

export const useCtmAdCRUDActions = () => {
  const dispatch: Dispatch<StoreState> = useDispatch();

  return useMemo(
    () => ({
      retrieveAll: (chatbotId: number) =>
        dispatch(ctmAdCRUDActions.retrieveAll(chatbotId)),
      retrieve: (chatbotId: number, id: number) =>
        dispatch(ctmAdCRUDActions.retrieveById(chatbotId, id)),
      update: (...params: Parameters<typeof ctmAdCRUDActions.update>) =>
        dispatch(ctmAdCRUDActions.update(...params)),
      create: (...params: Parameters<typeof ctmAdCRUDActions.create>) =>
        dispatch(ctmAdCRUDActions.create(...params)),
      delete: (...params: Parameters<typeof ctmAdCRUDActions.delete>) =>
        dispatch(ctmAdCRUDActions.delete(...params)),
    }),
    [dispatch],
  );
};

export default ctmAdCRUDActions;
